import { Role } from 'auth';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { SignUpPagesWrapper } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import { queryClient } from '@/modules/common/query.ts';
import { fileToBase64 } from '@/modules/common/utils/file.ts';
import {
  AUS_CARD_NUMBER_REGEX,
  AUS_LICENSE_REGEX,
  AUS_PASSPORT_NUMBER,
  NAME_REGEX,
  UKR_PASSPORT_NUMBER,
} from '@/modules/common/utils/regex.ts';
import DocumentDropzone from '@/modules/entity/components/DocumentDropzone.tsx';
import { VerificationLoadingScreen } from '@/modules/entity/components/VerificationLoadingScreen.tsx';
import {
  USER_ENTITIES_QUERY_KEY,
  useDeleteEntityDocumentMutation,
  useEntityFollowUpMutation,
  useEntityItemUncachedQuery,
} from '@/modules/entity/queries/entity.tsx';
import { entityPaths } from '@/modules/entity/routes.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { genderOptions } from '@/modules/entity/static/gender-options.ts';
import { streetTypeOptions, streetTypeOptionsUSA } from '@/modules/entity/static/street-types.ts';
import {
  EntityWithDocumentFullResponse,
  ShareholderDetails,
} from '@/modules/entity/types/EntityItemResponse.ts';
import {
  Alert,
  Button,
  CalendarInput,
  Dropzone,
  Select,
  SelectButtons,
  Spinner,
  TextInputField,
  getSelectValueByEnum,
} from 'ui';
import { AlertIcon } from 'ui/icons';
import { fixNameEnum } from 'ui/utils';
import {
  BronIDIndividual,
  BronIDRequestedActionsDocument,
  BronIDRequestedActionsIndividual,
  EntityDocumentWithUrl,
  EntityStatus,
  EntityType,
} from 'api';

dayjs.extend(customParseFormat);

interface FollowupDetailsFormik {
  requested_actions: (BronIDRequestedActionsDocument &
    (BronIDRequestedActionsIndividual & { fields: { documentType?: DocType; index?: string } }))[];
}

enum DocType {
  DriverLicense = 'driver_license',
  Passport = 'passport',
}

const documentOptions = [
  {
    label: 'Driver licence',
    value: DocType.DriverLicense,
  },
  {
    label: 'Passport',
    value: DocType.Passport,
  },
];

const entityTypeMatch: Partial<Record<EntityType, keyof EntityWithDocumentFullResponse>> = {
  [EntityType.Individual]: 'individual_entity_details',
  [EntityType.Company]: 'company_entity_details',
  [EntityType.Trust]: 'trust_entity_details',
  [EntityType.Smsf]: 'smsf_entity_details',
  [EntityType.Partnership]: 'partnership_entity_details',
  [EntityType.Association]: 'association_entity_details_id',
};

const DATE_OF_BIRTH_FORMAT = 'YYYY-MM-DD';
const DATE_OF_BIRTH_PARSE_FORMATS = ['DD/MM/YYYY', DATE_OF_BIRTH_FORMAT];
const ENTITY_REFETCH_INTERVAL = 1000 * 60;
const INDIVIDUAL_DOCUMENT_FIELDS = [
  'driversLicenceImageFront',
  'driversLicenceImageBack',
  'passportImage',
] as const;

const formatDateOfBirth = (date?: string) => {
  if (!date) return undefined;

  const parsedDate = dayjs(date, DATE_OF_BIRTH_PARSE_FORMATS, true);
  if (parsedDate.isValid()) return parsedDate.format(DATE_OF_BIRTH_FORMAT);

  if (date.includes('/')) return undefined;

  const normalizedDate = dayjs(date);

  return normalizedDate.isValid() ? normalizedDate.format(DATE_OF_BIRTH_FORMAT) : undefined;
};

const isExistingDocumentUrl = (value?: string) => value?.toLowerCase()?.startsWith('http');

const getValuesWithClearedDocument = (
  values: FollowupDetailsFormik,
  index: number,
  field?: keyof BronIDIndividual,
): FollowupDetailsFormik => ({
  ...values,
  requested_actions: values.requested_actions.map((action, actionIndex) => {
    if (actionIndex !== index) return action;

    if (field && 'fields' in action) {
      return {
        ...action,
        fields: {
          ...action.fields,
          [field]: '',
        },
      };
    }

    return {
      ...action,
      documentBase64: '',
    };
  }),
});

const mergeSavedValues = (
  generatedValues: FollowupDetailsFormik,
  saveValues?: FollowupDetailsFormik,
): FollowupDetailsFormik => {
  if (!saveValues) return generatedValues;

  return {
    requested_actions: generatedValues.requested_actions.map((generatedAction, index) => {
      const savedAction =
        saveValues.requested_actions.find(
          action => action.type === generatedAction.type && action.id === generatedAction.id,
        ) || saveValues.requested_actions[index];

      if (!savedAction) return generatedAction;

      if ('fields' in generatedAction && 'fields' in savedAction) {
        return {
          ...generatedAction,
          ...savedAction,
          fields: {
            ...generatedAction.fields,
            ...savedAction.fields,
          },
        };
      }

      return {
        ...generatedAction,
        ...savedAction,
      };
    }),
  };
};

const omitExistingDocumentUrls = (values: FollowupDetailsFormik): FollowupDetailsFormik => ({
  ...values,
  requested_actions: values.requested_actions.map(action => {
    if (!('fields' in action)) return action;

    const fields = { ...action.fields };

    INDIVIDUAL_DOCUMENT_FIELDS.forEach(field => {
      if (isExistingDocumentUrl(fields[field])) {
        delete fields[field];
      }
    });

    return {
      ...action,
      fields,
    };
  }),
});

// const shareholderByTypeMatch: Partial<
//   Record<
//     EntityType,
//     Array<
//       | ''
//       | 'trustee'
//       | 'beneficiaries'
//       | 'settlors'
//       | 'shareholders'
//       | 'directors'
//       | 'stakeholders'
//       | 'officers'
//     >
//   >
// > = {
//   [EntityType.Individual]: [''],
//   [EntityType.Company]: ['shareholders', 'directors'],
//   [EntityType.Trust]: ['trustee', 'beneficiaries', 'settlors', 'shareholders', 'directors'],
//   [EntityType.Smsf]: ['trustee', 'beneficiaries', 'settlors', 'shareholders', 'directors'],
//   [EntityType.Partnership]: ['stakeholders'],
//   [EntityType.Association]: ['shareholders', 'officers'],
// };

const BronIDIndividualSchema = yup.object<BronIDIndividual & { documentType: DocType }>({
  country: yup.string().trim().required('Country is required'),
  firstName: yup
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('First name is required'),
  lastName: yup
    .string()
    .trim()
    .min(2, 'Must be at least 2 characters')
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .required('Last name is required'),
  middleName: yup.string().trim(),
  gender: yup.string().trim().required('Gender is required'),
  dateOfBirth: yup
    .string()
    .trim()
    .required('Date of birth is required')
    .matches(/\d{4}-\d{2}-\d{2}/, 'Date of birth must be in the format YYYY-MM-DD'),
  unitNumber: yup.string().trim().required('Unit Number is required'),
  streetNumber: yup.string().trim().required('Street number is required'),
  streetName: yup.string().trim().required('Street name is required'),
  streetType: yup.string().trim().required('Street type is required'),
  suburb: yup.string().trim().required('Suburb is required'),
  postcode: yup.string().trim().required('Postcode is required'),
  state: yup.string().trim(),

  documentType: yup.mixed<DocType>().oneOf(Object.values(DocType)).default(DocType.Passport), //custom field on frontend

  driversLicenceImageFront: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.DriverLicense,
      then: schema => schema.required('Drivers licence front image is required'),
      otherwise: schema => schema.nullable(),
    }),
  driversLicenceImageBack: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.DriverLicense,
      then: schema => schema.required('Drivers licence back image is required'),
      otherwise: schema => schema.nullable(),
    }),
  driversLicenceNumber: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.DriverLicense,
      then: schema =>
        schema
          .matches(AUS_LICENSE_REGEX, 'Incorrect driver licence number, example: 123456')
          .required('Drivers licence number is required'),
      otherwise: schema => schema.nullable(),
    }),
  driversLicenceCardNumber: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.DriverLicense,
      then: schema =>
        schema
          .matches(
            AUS_CARD_NUMBER_REGEX,
            'Incorrect driver licence card number, example: 1234567890',
          )
          .required('Drivers licence card number is required'),
      otherwise: schema => schema.nullable(),
    }),
  driversLicenceState: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.DriverLicense,
      then: schema => schema.required('Drivers licence state is required'),
      otherwise: schema => schema.nullable(),
    }),

  passportImage: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.Passport,
      then: schema => schema.required('Passport image is required'),
      otherwise: schema => schema.nullable(),
    }),
  passportNumber: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.Passport,
      then: schema =>
        schema
          .when('passportCountryOfIssue', ([country], schema) => {
            if (country === 'AUS') {
              return schema.matches(
                AUS_PASSPORT_NUMBER,
                'Incorrect passport number, example: AB1234567',
              );
            }
            if (country === 'UKR' || country === 'NZL') {
              return schema.matches(
                UKR_PASSPORT_NUMBER,
                'Incorrect passport number, example: AB123456',
              );
            }
            // Return the scheme without changes if the country is not AUS or UKR
            return schema;
          })
          .required('Passport number is required'),
      otherwise: schema => schema.nullable(),
    }),
  passportCountryOfIssue: yup
    .string()
    .trim()
    .nullable()
    .when('documentType', {
      is: DocType.Passport,
      then: schema => schema.required('Passport country of issue is required'),
      otherwise: schema => schema.nullable(),
    }),
});

const BronIDRequestedActionsDocumentSchema = yup.object<BronIDRequestedActionsDocument>({
  type: yup.string().trim().required('Type is required'),
  id: yup.string().trim().required('ID is required'),
  extractType: yup.string().trim().required('Extract type is required'),
  documentBase64: yup.string().trim().required('Document is required'),
});

const BronIDRequestedActionsIndividualSchema = yup.object<BronIDRequestedActionsIndividual>({
  type: yup.string().trim().required('Type is required'),
  role: yup.string().trim().required('Role is required'),
  id: yup.string().trim().required('ID is required'),
  fields: BronIDIndividualSchema.required(),
});

const requestedActionsSchema = yup.array().of(
  yup.lazy(item => {
    if (item?.fields) {
      return BronIDRequestedActionsIndividualSchema;
    }
    return BronIDRequestedActionsDocumentSchema;
  }),
);

const validationSchema = yup.object<FollowupDetailsFormik>({
  requested_actions: requestedActionsSchema.required('At least one requested action is required'),
});

const initialValuesIndividual: BronIDRequestedActionsIndividual & {
  fields: { documentType?: DocType; index?: string };
} = {
  type: 'individual',
  role: 'shareholders',
  id: '',
  fields: {
    firstName: '',
    lastName: '',
    middleName: '',
    gender: genderOptions[0].value,
    dateOfBirth: '',
    country: 'AUS',
    unitNumber: '',
    streetNumber: '',
    streetName: '',
    streetType: '',
    suburb: '',
    state: '',
    postcode: '',
    documentType: DocType.Passport,
    driversLicenceImageFront: '',
    driversLicenceImageBack: '',
    driversLicenceNumber: '',
    driversLicenceCardNumber: '',
    driversLicenceState: '',
    passportImage: '',
    passportNumber: '',
    passportCountryOfIssue: '',
  },
};

const FollowUpDetails = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { id: entity_id = 0 } = useParams();
  const [requestedActions, setRequestedActions] =
    useState<
      (BronIDRequestedActionsDocument &
        (BronIDRequestedActionsIndividual & { fields: { documentType?: DocType | undefined } }))[]
    >();
  const [isFileLoading, setIsFileLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [entityDetails, setEntityDetails] = useState<any>();
  const [entityDocuments, setEntityDocuments] = useState<EntityDocumentWithUrl[]>([]);
  const [delDocIndex, setDelDocIndex] = useState<number | null>(null);
  const [saveValues, setSaveValues] = useState<FollowupDetailsFormik | undefined>();

  const generateInitialValues = (): FollowupDetailsFormik => {
    const generatedValues: FollowupDetailsFormik = {
      requested_actions:
        requestedActions?.map(({ type, ...actions }) => {
          if ('fields' in actions && type === 'individual') {
            const { dateOfBirth } = actions.fields;
            const { index, date_of_birth, ...roleDate } =
              (entityDetails?.[actions?.role]?.filter(
                //ToDo: Add recursive find shareholder info !Too hard and long work
                ({ first_name = '', last_name = '' }) =>
                  first_name?.toUpperCase()?.includes(actions.fields?.firstName?.toUpperCase()) &&
                  (last_name?.toUpperCase()?.includes(actions.fields?.lastName?.toUpperCase()) ||
                    last_name?.toUpperCase()?.includes(actions.fields?.middleName?.toUpperCase())),
              )[0] as ShareholderDetails) || {};

            // ToDo: find document by index shareholder and index find by first name and last name
            const driversLicenceImageBack = entityDocuments.find(
              item =>
                item.type === 'driver_license_back' &&
                (item.index === `${actions?.role ? actions?.role + '_' : ''}${actions.id}` ||
                  item.index === `${actions?.role ? actions?.role + '_' : ''}${index}` ||
                  item.index === actions.id ||
                  item.index === index), //index = "stakeholders_220ae753-d567-4b3d-a14e-ae86f46e0b15"
            )?.url;
            const driversLicenceImageFront = entityDocuments.find(
              item =>
                item.type === 'driver_license_front' &&
                (item.index === `${actions?.role ? actions?.role + '_' : ''}${actions.id}` ||
                  item.index === `${actions?.role ? actions?.role + '_' : ''}${index}` ||
                  item.index === actions.id ||
                  item.index === index), //index = "stakeholders_220ae753-d567-4b3d-a14e-ae86f46e0b15"
            )?.url;
            const passportImage = entityDocuments.find(
              item =>
                item.type === 'passport' &&
                (item.index === `${actions?.role ? actions?.role + '_' : ''}${actions.id}` ||
                  item.index === `${actions?.role ? actions?.role + '_' : ''}${index}` ||
                  item.index === actions.id ||
                  item.index === index), //index = "stakeholders_220ae753-d567-4b3d-a14e-ae86f46e0b15"
            )?.url;

            const individualDetail = {
              type,
              ...actions,
              fields: {
                ...initialValuesIndividual.fields,
                index,
                ...roleDate,
                ...actions?.fields,
                passportCountryOfIssue:
                  actions?.fields?.passportCountryOfIssue ?? roleDate?.country_of_issue,
                passportNumber: actions?.fields?.passportNumber ?? roleDate?.passport_number,
                driversLicenceCardNumber:
                  actions?.fields?.driversLicenceCardNumber ?? roleDate?.driver_license_card_number,
                driversLicenceNumber:
                  actions?.fields?.driversLicenceNumber ?? roleDate?.driver_license_number,
                driversLicenceState:
                  actions?.fields?.driversLicenceState ?? roleDate?.drivers_license_state,
                firstName: actions?.fields?.firstName ?? roleDate?.first_name,
                lastName: actions?.fields?.lastName ?? roleDate?.last_name,
                middleName: actions?.fields?.middleName ?? roleDate?.middle_name,
                unitNumber: actions?.fields?.unitNumber ?? roleDate?.unit_number,
                streetName: actions?.fields?.streetName ?? roleDate?.street_name,
                streetNumber: actions?.fields?.streetNumber ?? roleDate?.street_number,
                streetType: actions?.fields?.streetType ?? roleDate?.street_type,
                country: actions?.fields?.country ?? roleDate?.country,
                state: actions?.fields?.state ?? roleDate?.state,
                suburb: actions?.fields?.suburb ?? roleDate?.suburb,
                postcode: actions?.fields?.postcode ?? roleDate?.postcode,
                gender: actions?.fields?.gender ?? roleDate?.gender,
                dateOfBirth:
                  dateOfBirth || date_of_birth
                    ? formatDateOfBirth(dateOfBirth || date_of_birth)
                    : undefined,
                passportImage,
                driversLicenceImageFront,
                driversLicenceImageBack,
                documentType:
                  !!driversLicenceImageFront && !!driversLicenceImageBack
                    ? DocType.DriverLicense
                    : DocType.Passport,
              },
            } as BronIDRequestedActionsDocument &
              (BronIDRequestedActionsIndividual & {
                fields: { documentType?: DocType | undefined };
              });

            return individualDetail;
          }

          return { type: type, ...actions } as BronIDRequestedActionsDocument &
            (BronIDRequestedActionsIndividual & {
              fields: { documentType?: DocType | undefined };
            });
        }) || [],
    };

    return mergeSavedValues(generatedValues, saveValues);
  };

  const formik = useFormik<FollowupDetailsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      await followUp({ ...omitExistingDocumentUrls(values), entity_id: +entity_id });
    },
    enableReinitialize: true,
  });
  const formikSnapshotRef = useRef({ dirty: formik.dirty, values: formik.values });

  const { mutateAsync: followUp, isLoading: isFollowUpLoading } = useEntityFollowUpMutation({
    setErrors: formik.setErrors,
  });
  const { data: entity, isLoading: isEntityLoading } = useEntityItemUncachedQuery(
    +entity_id,
    false,
    { refetchInterval: ENTITY_REFETCH_INTERVAL },
  );

  const { mutateAsync: deleteDocument, isLoading: isDocumentDeletion } =
    useDeleteEntityDocumentMutation();

  useEffect(() => {
    formikSnapshotRef.current = { dirty: formik.dirty, values: formik.values };
  }, [formik.dirty, formik.values]);

  const handleChangeSelect = (field: string, index: number, value: string) => {
    formik.setFieldValue(`requested_actions[${index}].fields.${field}`, value);
    formik.setFieldError(`requested_actions[${index}].fields.${field}`, '');
  };

  const handleChangeType = (docType: DocType, index: number) => {
    handleChangeSelect('documentType', index, docType);
  };

  const onFileAccepted = async (file: File, index: number) => {
    setIsFileLoading(true);

    const values = formik.values.requested_actions[index] as BronIDRequestedActionsDocument;
    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue(`requested_actions[${index}]`, {
      ...values,
      documentBase64: base64,
    });
    formik.setFieldError(`requested_actions[${index}].documentBase64`, '');

    setIsFileLoading(false);
  };

  const onFileAcceptedIndividual = async (file: File, index: number, field: string) => {
    setIsFileLoading(true);

    const base64 = await fileToBase64(file, true);

    await formik.setFieldValue(`requested_actions[${index}].fields.${field}`, base64);
    formik.setFieldError(`requested_actions[${index}].fields.${field}`, '');

    setIsFileLoading(false);
  };

  const onFileClear = useCallback(
    async (index: number) => {
      setDelDocIndex(index);
      const values = formik.values.requested_actions[index];
      const nextValues = getValuesWithClearedDocument(formik.values, index);
      const { id, documentBase64, role, fields } = values;

      const doc = entityDocuments.find(
        item =>
          item.index === `${role ? role + '_' : ''}${id}` ||
          item.index === id ||
          item.index === `${role ? role + '_' : ''}${fields?.index}` ||
          item.index === fields?.index, //index = "stakeholders_220ae753-d567-4b3d-a14e-ae86f46e0b15"
      );

      if (
        documentBase64?.toLowerCase()?.startsWith('http') &&
        entity_id &&
        doc?.index &&
        doc?.type
      ) {
        setSaveValues(nextValues);
        await deleteDocument({
          type: doc?.type,
          index: doc?.index,
          entity_id: +entity_id,
        });
      }

      await formik.setValues(nextValues, false);
      formik.setFieldError(`requested_actions[${index}].documentBase64`, '');
      setDelDocIndex(null);
    },
    [deleteDocument, entityDocuments, entity_id, formik],
  );

  const onFileClearIndividual = useCallback(
    async (index: number, field: keyof BronIDIndividual) => {
      setDelDocIndex(index);
      const values = formik.values.requested_actions[index];
      const nextValues = getValuesWithClearedDocument(formik.values, index, field);
      const { id, fields, role } = values;

      const documentField = fields?.[field];

      const doc = entityDocuments.find(
        item =>
          item.index === `${role ? role + '_' : ''}${id}` ||
          item.index === id ||
          item.index === `${role ? role + '_' : ''}${fields?.index}` ||
          item.index === fields?.index, //index = "stakeholders_220ae753-d567-4b3d-a14e-ae86f46e0b15"
      );
      if (
        documentField?.toLowerCase()?.startsWith('http') &&
        entity_id &&
        doc?.index &&
        doc?.type
      ) {
        setSaveValues(nextValues);
        await deleteDocument({
          type: doc?.type,
          index: doc?.index,
          entity_id: +entity_id,
        });
      }

      await formik.setValues(nextValues, false);
      formik.setFieldError(`requested_actions[${index}].fields.${field}`, '');
      setDelDocIndex(null);
    },
    [deleteDocument, entityDocuments, entity_id, formik],
  );

  const handleChangeDate = (date: dayjs.Dayjs, index: number) => {
    formik.setFieldValue(
      `requested_actions[${index}].fields.dateOfBirth`,
      date ? date.format('YYYY-MM-DD').toString() : '',
    );
    formik.setFieldError(`requested_actions[${index}].fields.dateOfBirth`, '');
  };

  useEffect(() => {
    if (!entity?.entity) return;

    const formikSnapshot = formikSnapshotRef.current;
    if (formikSnapshot.dirty && formikSnapshot.values.requested_actions.length) {
      setSaveValues(formikSnapshot.values);
    }

    setRejectReason(entity.entity.entity_application_rejection_reason || '');
    setRequestedActions(entity.entity.bron_id_requested_actions);
    setEntityDetails(
      entity.entity[
        entityTypeMatch[entity.entity.type as EntityType] as keyof EntityWithDocumentFullResponse
      ],
    );
    setEntityDocuments(entity.entity.document_files || []);
  }, [entity?.entity]); //don't add formik.values.requested_actions

  const hasIndividuals = useMemo(
    () => formik.values.requested_actions.some(a => a.type === 'individual'),
    [formik.values.requested_actions],
  );

  const handleBack = useCallback(() => {
    navigate(
      { pathname: entityPaths.static.entityList, search: params.toString() },
      {
        replace: false,
      },
    );
  }, [navigate, params]);

  const mainAction = useMemo(
    () => ({
      name: 'Back to entities list',
      onClick: handleBack,
    }),
    [handleBack],
  );

  useEffect(() => {
    console.log('Errors: ', formik.errors);
  }, [formik.errors]);

  if (
    (entity?.entity?.status !== EntityStatus.EntityUnsuccessfull ||
      entity?.entity?.bron_id_requested_actions?.length === 0) &&
    isEntityLoading === false &&
    !!entity?.entity
  ) {
    queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);
    toast.success('This entity does not require a follow-up. Please wait for the status update.', {
      id: 'follow-up',
      duration: 5000,
    });
    return <Navigate to={{ pathname: entityPaths.static.entityList, search: params.toString() }} />;
  }

  return (
    <SignUpPagesWrapper
      className="justify-start"
      backAction={{ onClick: handleBack }}
      mainAction={mainAction}
    >
      <div className="max-w-[660px] w-full flex-1 flex flex-col items-center justify-center px-6">
        {isEntityLoading ? (
          <Spinner size="lg" variant="primary" />
        ) : isFollowUpLoading ? (
          <VerificationLoadingScreen />
        ) : (
          <FormikProvider value={formik}>
            <div className="w-full flex flex-col gap-6">
              {!(
                rejectReason.includes('Probably you made a mistake while filling it out') &&
                !requestedActions?.length
              ) && (
                <Alert
                  icon={AlertIcon}
                  variant="error"
                  label={rejectReason
                    ?.replace('a document additional_document', 'an additional document')
                    ?.replace(/([^\r\n])\s*(Some of the fields)/g, '$1\n$2')}
                  className="mb-0"
                />
              )}
              {hasIndividuals && (
                <Alert icon={AlertIcon} variant="neutral-2" className="mb-0">
                  <p>
                    <b>Most common mistakes for individuals:</b> Incorrect date of birth,
                    passport/driving licence numbers, absent or incorrectly placed middle name.
                    Please try again and in case of any issuers contact{' '}
                    <a
                      href="mailto:support@complione.com.au"
                      className="text-blue-500 hover:underline"
                    >
                      support@complione.com.au
                    </a>
                  </p>
                </Alert>
              )}
              {!requestedActions?.length && (
                <Alert icon={AlertIcon} variant="neutral-2" className="mb-0">
                  <p>
                    Your entity has been declined by the platform, please try a different entity. If
                    you have any questions, you can reach out to our support by this email{' '}
                    <a
                      href="mailto:support@complione.com.au"
                      className="text-blue-500 hover:underline"
                    >
                      support@complione.com.au
                    </a>
                  </p>
                </Alert>
              )}

              <div className="w-full flex flex-col gap-5">
                {formik.values.requested_actions.map(({ type, ...actions }, index) => {
                  if ('fields' in actions && type === 'individual') {
                    const values = formik.values.requested_actions[
                      index
                    ] as BronIDRequestedActionsIndividual & { fields: { documentType?: DocType } };
                    const errors = formik.errors.requested_actions?.[index];
                    const errorsFields =
                      errors && typeof errors !== 'string' && 'fields' in errors && errors?.fields
                        ? errors?.fields
                        : undefined;

                    const documentType = values.fields.documentType;
                    return (
                      <div
                        className="w-full flex flex-col gap-3 p-10 rounded-xl border border-gray-200"
                        key={'person' + index}
                      >
                        <p className="text-2xl capitalize">
                          {actions.role.substring(0, actions.role.length - 1)}{' '}
                          {actions.fields['firstName'].toLowerCase()}{' '}
                          {actions.fields['lastName'].toLowerCase()}
                        </p>
                        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
                          <Select
                            label="gender"
                            options={genderOptions}
                            value={values.fields.gender}
                            error={errorsFields?.gender}
                            onChange={value => handleChangeSelect('gender', index, value || '')}
                            positionMenu="up"
                          />
                          <TextInputField
                            label="first name"
                            name={`requested_actions[${index}].fields.firstName`}
                          />
                        </div>
                        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
                          <TextInputField
                            label="Middle name (optional)"
                            name={`requested_actions[${index}].fields.middleName`}
                          />
                          <TextInputField
                            label="Last name"
                            name={`requested_actions[${index}].fields.lastName`}
                          />
                        </div>
                        <CalendarInput
                          label="date of birth"
                          defaultValue={
                            values.fields.dateOfBirth ? dayjs(values.fields.dateOfBirth) : undefined
                          }
                          onChange={value => handleChangeDate(value, index)}
                        />
                        <Select
                          label="country"
                          options={countryOptions}
                          value={values.fields.country}
                          onChange={value => handleChangeSelect('country', index, value || '')}
                          error={errorsFields?.country}
                          positionMenu="up"
                        />
                        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
                          <TextInputField
                            label="unit number"
                            name={`requested_actions[${index}].fields.unitNumber`}
                          />
                          <TextInputField
                            label="street number"
                            name={`requested_actions[${index}].fields.streetNumber`}
                          />
                        </div>
                        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
                          <TextInputField
                            label="street Name"
                            name={`requested_actions[${index}].fields.streetName`}
                          />
                          <Select
                            label="street Type"
                            options={
                              values.fields.country === 'AUS'
                                ? streetTypeOptions
                                : streetTypeOptionsUSA
                            }
                            value={values.fields.streetType}
                            error={errorsFields?.streetType}
                            onChange={value => handleChangeSelect('streetType', index, value || '')}
                            positionMenu="up"
                          />
                        </div>
                        <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
                          <TextInputField
                            label="suburb"
                            name={`requested_actions[${index}].fields.suburb`}
                          />
                          {values.fields.country === 'AUS' && (
                            <Select
                              label="state"
                              options={auStateOptions}
                              value={values.fields.state}
                              error={errorsFields?.state}
                              onChange={value => handleChangeSelect('state', index, value || '')}
                              positionMenu="up"
                            />
                          )}
                          <div className="sm:max-w-[20%] sm:overflow-hidden">
                            <TextInputField
                              label="postcode"
                              className="min-w-0"
                              name={`requested_actions[${index}].fields.postcode`}
                            />
                          </div>
                        </div>
                        {values.fields.country === 'AUS' && (
                          <>
                            <p className="text-[24px] leading-[36px] mt-[32px]"></p>
                            <Alert
                              label="At least one document type, passport or driving licence. If you didn't upload your documents before, very likely by adding them the verification is going to be completed."
                              variant="neutral-2"
                            />
                            <SelectButtons
                              options={documentOptions}
                              selected={getSelectValueByEnum<DocType>(documentType ?? '', DocType)}
                              onChange={value => handleChangeType(value, index)}
                            />
                            {documentType === DocType.DriverLicense && (
                              <>
                                <TextInputField
                                  name={`requested_actions[${index}].fields.driversLicenceNumber`}
                                  label="drivers licence Number"
                                />
                                <TextInputField
                                  name={`requested_actions[${index}].fields.driversLicenceCardNumber`}
                                  label="drivers licence Card Number"
                                />
                                <Select
                                  options={auStateOptions}
                                  label="drivers licence state"
                                  onChange={value =>
                                    handleChangeSelect('driversLicenceState', index, value || '')
                                  }
                                  value={values.fields.driversLicenceState}
                                  error={errorsFields?.driversLicenceState}
                                  positionMenu="up"
                                />
                                <div className="w-full flex flex-col space-y-1">
                                  <p className="text-[14px] leading-[22px]">Image Front</p>
                                  <p className="text-sm leading-5">JPG, PNG or PDF. Up to 10 mb</p>
                                </div>
                                <DocumentDropzone
                                  type="driver_license_front"
                                  initDocs={[]}
                                  entityId={+entity_id}
                                  onFileAccepted={value =>
                                    onFileAcceptedIndividual(
                                      value,
                                      index,
                                      'driversLicenceImageFront',
                                    )
                                  }
                                  onFileClear={() =>
                                    onFileClearIndividual(index, 'driversLicenceImageFront')
                                  }
                                  value={values.fields.driversLicenceImageFront}
                                  error={errorsFields?.driversLicenceImageFront}
                                  loading={isDocumentDeletion && delDocIndex === index}
                                />
                                <div className="w-full flex flex-col space-y-1">
                                  <p className="text-[14px] leading-[22px]">Image Back</p>
                                  <p className="text-3 leading-5">JPG, PNG or PDF. Up to 10 mb</p>
                                </div>
                                <DocumentDropzone
                                  type="driver_license_back"
                                  initDocs={[]}
                                  entityId={+entity_id}
                                  onFileAccepted={value =>
                                    onFileAcceptedIndividual(
                                      value,
                                      index,
                                      'driversLicenceImageBack',
                                    )
                                  }
                                  onFileClear={() =>
                                    onFileClearIndividual(index, 'driversLicenceImageBack')
                                  }
                                  value={values.fields.driversLicenceImageBack}
                                  error={errorsFields?.driversLicenceImageBack}
                                  loading={isDocumentDeletion && delDocIndex === index}
                                />
                              </>
                            )}
                            {documentType === DocType.Passport && (
                              <>
                                <TextInputField
                                  name={`requested_actions[${index}].fields.passportNumber`}
                                  label="passport number"
                                />
                                <Select
                                  options={countryOptions}
                                  label="country of issue"
                                  onChange={value =>
                                    handleChangeSelect('passportCountryOfIssue', index, value || '')
                                  }
                                  value={values.fields.passportCountryOfIssue}
                                  error={errorsFields?.passportCountryOfIssue}
                                  positionMenu="up"
                                />
                                <div className="w-full flex flex-col space-y-1">
                                  <p className="text-[14px] leading-[22px]">Image</p>
                                  <p className="text-sm leading-5">JPG, PNG or PDF. Up to 10 mb</p>
                                </div>
                                <DocumentDropzone
                                  type="passport"
                                  initDocs={[]}
                                  entityId={+entity_id}
                                  onFileAccepted={value =>
                                    onFileAcceptedIndividual(value, index, 'passportImage')
                                  }
                                  onFileClear={() => onFileClearIndividual(index, 'passportImage')}
                                  value={values.fields.passportImage}
                                  error={errorsFields?.passportImage}
                                  loading={isDocumentDeletion && delDocIndex === index}
                                />
                              </>
                            )}
                          </>
                        )}
                      </div>
                    );
                  }

                  if (type === 'document') {
                    const values = formik.values.requested_actions[
                      index
                    ] as BronIDRequestedActionsDocument;
                    const errors = formik.errors.requested_actions?.[index];
                    const errorsDocument =
                      errors && typeof errors !== 'string' && 'documentBase64' in errors
                        ? errors
                        : undefined;

                    return (
                      <div className="w-full flex flex-col space-y-3" key={'doc' + index}>
                        <p className="text-2xl capitalize">
                          {fixNameEnum(values['extractType' as keyof typeof values])}
                        </p>
                        <Dropzone //ToDo: change to DocumentDropzone. i don't know type document
                          onFileAccepted={file => onFileAccepted(file, index)}
                          onFileClear={() => onFileClear(index)}
                          loading={isFileLoading || isDocumentDeletion}
                          value={values.documentBase64}
                          error={errorsDocument?.documentBase64}
                        />
                      </div>
                    );
                  }
                })}
              </div>
              <div className="w-full flex flex-col items-center justify-between gap-4">
                {requestedActions?.length && (
                  <Button
                    className="w-full relative"
                    type="submit"
                    onClick={formik.handleSubmit}
                    disabled={isFollowUpLoading}
                    loading={isFollowUpLoading}
                  >
                    Submit follow up verification
                  </Button>
                )}
                <Button
                  className="w-full"
                  variant="secondary"
                  type="submit"
                  onClick={() =>
                    navigate(
                      { pathname: entityPaths.static.entityList, search: params.toString() },
                      {
                        replace: false,
                      },
                    )
                  }
                >
                  Go back
                </Button>
              </div>
            </div>
          </FormikProvider>
        )}
      </div>
    </SignUpPagesWrapper>
  );
};

export default withPrivateAccess(FollowUpDetails, Role.SR_INVESTOR);
